import { defaultMemberSection, memberSectionSchema, MemberSectionType } from "@/api/mem/memberSection";
import { useChangeMemberSectionMutation } from "@/api/mem/memberSectionQueryOptions";
import { membershipsQueryOptions } from "@/api/membershipsQueryOptions";
import { ApiErrorComponent } from "@/components/ApiErrorComponent";
import LoadProgress from "@/components/LoadProgress";
import SelectSection from "@/components/select/SelectSection";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, Chip, Grid, List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { t } from "i18next";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";


export const Route = createFileRoute('/_auth/bulkUpdateSection')({
    component: BulkUpdateSectionComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
});


function BulkUpdateSectionComponent() {

    const [checked, setChecked] = React.useState<string[]>([]);
    const [selectedSourceUuid, setSelectedSourceUuid] = React.useState<string>();
    const [selectedTargetUuid, setSelectedTargetUuid] = React.useState<string>();
    const [qualifyingDate, setQualifyingDate] = React.useState<Date>(new Date());

    const sectionMemberQuery = useSuspenseQuery(membershipsQueryOptions(selectedSourceUuid ? [{ field: 'properties.section', value: selectedSourceUuid, operator: 'isAnyOf' }, { field: 'active', value: 'true', operator: 'is' }] : [{ field: 'properties.hasSection', value: 'true', operator: 'is' }, { field: 'active', value: 'true', operator: 'is' }], [{ field: 'member.birthdate', direction: 'asc' }], [{ field: 'properties.section' }], 0, 150))
    const sectionMemberRaw = sectionMemberQuery.data.content
    const sectionMember = sectionMemberRaw.filter((membership) => { const sec = JSON.parse(membership.properties.section); return sec.endDate == null })

    const { control: sourceControl, watch: watchSource } = useForm<MemberSectionType>({
        mode: 'onBlur',
        resolver: yupResolver<MemberSectionType, any, any>(memberSectionSchema),
        defaultValues: defaultMemberSection
    })

    useEffect(() => {
        const subscription = watchSource((value) => {
            setSelectedSourceUuid(value.section?.uuid || 'unknown')
        })

        return () => subscription.unsubscribe();

    }, [watchSource])

    const { control: targetControl, watch: watchTarget } = useForm<MemberSectionType>({
        mode: 'onBlur',
        resolver: yupResolver<MemberSectionType, any, any>(memberSectionSchema),
        defaultValues: defaultMemberSection
    })


    useEffect(() => {
        const subscription = watchTarget((value) => {
            setSelectedTargetUuid(value.section?.uuid || 'unknown')
        })

        return () => subscription.unsubscribe();

    }, [watchTarget])

    const handleToggle = (value: string) => () => {
        const currentIndex = checked.indexOf(value)
        const newChecked = [...checked]

        if (currentIndex === -1) {
            newChecked.push(value)
        } else {
            newChecked.splice(currentIndex, 1)
        }

        setChecked(newChecked)
    }

    const onChanged = () => {
        enqueueSnackbar(t('action.changed', { entity: t('entity.memberSection') }));
    }
    const onError = () => { }

    const changeMemberSection = useChangeMemberSectionMutation(onChanged, onError)

    const bulkChange = () => {
        if (selectedSourceUuid && selectedTargetUuid) {
            for (const memberUuid of checked) {
                changeMemberSection.mutate({ memberUuid, oldSectionUuid: selectedSourceUuid, newSectionUuid: selectedTargetUuid, date: qualifyingDate })
            }
        }
        setChecked([])
    }

    if (sectionMemberQuery.isLoading || sectionMemberQuery.isFetching || !sectionMember) {
        return <LoadProgress />
    }

    return (
        <>
            <Grid container spacing={1}>
                <Grid size={{
                    lg: 3
                }}>
                    <SelectSection control={sourceControl} name="section.uuid" optional={false} label='Aktuelle Stufe' />
                </Grid>
                <Grid size={{
                    lg: 3
                }}>
                    <DatePicker
                        defaultValue={dayjs(qualifyingDate)}
                        onChange={(date) => setQualifyingDate(date?.toDate() || new Date())}
                        label="Stichtag"
                        slotProps={{
                            textField: {
                                variant: "standard",
                            }
                        }}
                    />
                </Grid>
                <Grid size={{
                    lg: 3
                }}>
                    <SelectSection control={targetControl} name="section.uuid" optional={false} filter='assignable' label='Zielstufe' />
                </Grid>
                <Grid
                    size={{
                        lg: 3
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end'
                    }}>
                    <Button variant="outlined" color="primary" onClick={bulkChange}>Überstellen</Button>
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid size={{
                    lg: 3,
                    md: 6,
                    sm: 12
                }} >
                    <List dense>
                        {
                            sectionMember?.map((membership) => {

                                const age = dayjs(qualifyingDate).diff(dayjs(membership.member.birthdate), 'year', true)
                                const section = membership.properties.section
                                const sec = JSON.parse(section)

                                return <ListItem key={membership.uuid} disablePadding>
                                    <ListItemButton role={undefined} onClick={handleToggle(membership.member.uuid)} dense>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={checked.indexOf(membership.member.uuid) !== -1}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={`${membership.member.lastname} ${membership.member.firstname}`}
                                            secondary={`${membership.member.birthdate && dayjs(membership.member.birthdate).format('DD.MM.YYYY')} (${Math.floor(age)} Jahre)`}
                                        >
                                        </ListItemText>
                                        <ListItemAvatar>
                                            {
                                                sec.section && <Chip color="primary" style={{ backgroundColor: sec.section.color }} label={sec.section.abbreviation} size="small" />
                                            }

                                        </ListItemAvatar>
                                    </ListItemButton>
                                </ListItem>
                            })
                        }
                    </List>
                </Grid>
            </Grid>
        </>
    );
}